import React, { useState, useEffect } from 'react';
import {
  Tabs,
  Card,
  Table,
  Button,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
  Typography,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Alert,
  Spin,
  Divider,
  message
} from 'antd';
import {
  DownloadOutlined,
  PlusOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  BankOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  WalletOutlined,
  HistoryOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

// Instance khusus merchant: kalau token expired/invalid (401) => balik ke login
const merchantApi = axios.create();
merchantApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('merchant_access_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('merchant_refresh_token');
      localStorage.removeItem('merchant_user');
      message.error('Sesi Anda telah berakhir, silakan login kembali');
      window.location.href = '/merchant/login';
    }
    return Promise.reject(error);
  }
);

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const MerchantFinance = () => {
  const [loading, setLoading] = useState(false);
  const [balanceMutations, setBalanceMutations] = useState([]);
  const [depositTickets, setDepositTickets] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [depositBanks, setDepositBanks] = useState([]);
  const [summary, setSummary] = useState({});
  const balance = summary?.balance || {};
  const [createDepositVisible, setCreateDepositVisible] = useState(false);
  const [mutationFilters, setMutationFilters] = useState({
    page: 1,
    limit: 20,
    type: '',
    reference_type: '',
    date_from: '',
    date_to: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const [depositForm] = Form.useForm();

  useEffect(() => {
    fetchFinanceSummary();
    fetchBalanceMutations();
    fetchDepositTickets();
    fetchSettlements();
    fetchDepositBanks();
  }, []);

  useEffect(() => {
    fetchBalanceMutations();
  }, [mutationFilters]);

  const fetchFinanceSummary = async () => {
    try {
      const token = localStorage.getItem('merchant_access_token') || localStorage.getItem('access_token');
      if (!token) {
        console.error('No token found');
        return;
      }

      const response = await merchantApi.get('/api/merchant/finance/summary', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Finance summary from API:', response.data);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch finance summary:', error.message);

      // Use empty data for new merchant
      const emptyData = {
        balance: {
          available_balance: 0,
          pending_balance: 0,
          total_earned: 0,
          total_settled: 0
        },
        today_activity: {
          today_transactions: 0,
          today_credit: 0,
          today_debit: 0
        },
        pending_deposits: {
          pending_deposits: 0,
          pending_amount: 0
        },
        this_month_settlement: {
          settled_count: 0,
          settled_amount: 0
        }
      };

      console.log('Using empty data for new merchant');
      setSummary(emptyData);
    }
  };

  const fetchBalanceMutations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('merchant_access_token') || localStorage.getItem('access_token');
      console.log('Balance mutations - Token available:', !!token);

      const response = await merchantApi.get('/api/merchant/finance/balance-mutations', {
        params: mutationFilters,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setBalanceMutations(response.data.mutations);
      setPagination({
        current: response.data.pagination.currentPage,
        pageSize: response.data.pagination.itemsPerPage,
        total: response.data.pagination.totalItems
      });
      console.log('Balance mutations loaded successfully');
    } catch (error) {
      console.error('Failed to fetch balance mutations:', error.message);
      console.log('Using empty data for balance mutations');
      // Set empty data for new merchant
      setBalanceMutations([]);
      setPagination({
        current: 1,
        pageSize: 20,
        total: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Unduh seluruh mutasi saldo (mengikuti filter aktif) sebagai CSV.
  const handleExportMutations = async () => {
    try {
      const token = localStorage.getItem('merchant_access_token') || localStorage.getItem('access_token');
      const response = await merchantApi.get('/api/merchant/finance/balance-mutations', {
        params: { ...mutationFilters, page: 1, limit: 100000 },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const rows = (response.data && response.data.mutations) || [];
      if (!rows.length) {
        message.warning('Tidak ada data mutasi untuk diunduh');
        return;
      }
      const esc = (v) => `"${String(v === null || v === undefined ? '' : v).replace(/"/g, '""')}"`;
      const header = ['Waktu', 'Tipe', 'Nominal', 'Saldo Sebelum', 'Saldo Sesudah', 'Kategori', 'Referensi', 'Keterangan'];
      const lines = rows.map((r) => [
        r.created_at, r.mutation_type, r.amount, r.balance_before, r.balance_after,
        r.balance_category || '', r.reference_id || '', r.description || ''
      ].map(esc).join(','));
      const csv = '﻿' + [header.map(esc).join(','), ...lines].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `mutasi_saldo_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      message.success(`${rows.length} baris mutasi diunduh`);
    } catch (error) {
      console.error('Export mutasi error:', error);
      message.error('Gagal mengunduh mutasi saldo');
    }
  };

  const fetchDepositTickets = async () => {
    try {
      const token = localStorage.getItem('merchant_access_token') || localStorage.getItem('access_token');
      console.log('Deposit tickets - Token available:', !!token);

      const response = await merchantApi.get('/api/merchant/finance/deposit-tickets', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setDepositTickets(response.data.tickets);
      console.log('Deposit tickets loaded successfully');
    } catch (error) {
      console.error('Failed to fetch deposit tickets:', error.message);
      console.log('Using empty data for deposit tickets');
      // Set empty data for new merchant
      setDepositTickets([]);
    }
  };

  const fetchSettlements = async () => {
    try {
      const token = localStorage.getItem('merchant_access_token') || localStorage.getItem('access_token');
      console.log('Settlements - Token available:', !!token);

      const response = await merchantApi.get('/api/merchant/finance/settlements', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSettlements(response.data.settlements);
      console.log('Settlements loaded successfully');
    } catch (error) {
      console.error('Failed to fetch settlements:', error.message);
      console.log('Using mock data for settlements');
      // Set empty data for new merchant
      setSettlements([]);
    }
  };

  const fetchDepositBanks = async () => {
    try {
      const response = await merchantApi.get('/api/deposit-banks');
      setDepositBanks(response.data.banks || []);
      console.log('Deposit banks loaded successfully');
    } catch (error) {
      console.error('Failed to fetch deposit banks:', error.message);
      setDepositBanks([]);
    }
  };


  const handleCreateDepositTicket = async (values) => {
    try {
      const token = localStorage.getItem('merchant_access_token') || localStorage.getItem('access_token');
      const response = await merchantApi.post('/api/merchant/finance/deposit-tickets', values, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      message.success('Tiket deposit berhasil dibuat');

      // Show ticket details
      Modal.info({
        title: 'Tiket Deposit Berhasil Dibuat',
        content: (
          <div>
            <Alert
              message="Penting!"
              description="Silakan transfer sesuai jumlah unik di bawah ini untuk verifikasi otomatis."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <div><Text strong>Nomor Tiket:</Text> {response.data.ticket.ticket_number}</div>
            <div><Text strong>Jumlah Transfer:</Text> Rp {response.data.ticket.unique_amount.toLocaleString('id-ID')}</div>
            <div><Text strong>Bank Tujuan:</Text> {response.data.ticket.bank_account}</div>
            <div><Text strong>Berlaku Hingga:</Text> {dayjs(response.data.ticket.expires_at).format('DD/MM/YYYY HH:mm')}</div>
          </div>
        ),
        width: 600
      });

      setCreateDepositVisible(false);
      depositForm.resetFields();
      fetchDepositTickets();
    } catch (error) {
      console.error('Create deposit ticket error:', error);
      message.error(error.response?.data?.error || 'Gagal membuat tiket deposit');
    }
  };

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(numericAmount);
  };

  const getMutationTypeColor = (type) => {
    return type === 'credit' ? 'green' : 'red';
  };

  const getReferenceTypeColor = (type) => {
    const colors = {
      deposit: 'blue',
      withdrawal: 'orange',
      transaction_fee: 'purple',
      settlement: 'cyan',
      refund: 'green',
      adjustment: 'gray'
    };
    return colors[type] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      confirmed: 'green',
      expired: 'red',
      cancelled: 'gray',
      processing: 'blue',
      completed: 'green',
      failed: 'red'
    };
    return colors[status] || 'default';
  };

  // Balance Mutations Table Columns
  const mutationColumns = [
    {
      title: 'Tanggal',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      width: 140,
    },
    {
      title: 'Tipe',
      dataIndex: 'mutation_type',
      key: 'mutation_type',
      render: (type) => (
        <Tag color={getMutationTypeColor(type)} icon={type === 'credit' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}>
          {type === 'credit' ? 'Kredit' : 'Debit'}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Kategori',
      dataIndex: 'reference_type',
      key: 'reference_type',
      render: (type) => (
        <Tag color={getReferenceTypeColor(type)}>
          {type.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
      width: 120,
    },
    {
      title: 'Jumlah',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => (
        <Text strong style={{ color: record.mutation_type === 'credit' ? '#52c41a' : '#ff4d4f' }}>
          {record.mutation_type === 'credit' ? '+' : '-'} {formatCurrency(amount)}
        </Text>
      ),
      width: 120,
    },
    {
      title: 'Saldo Sebelum',
      dataIndex: 'balance_before',
      key: 'balance_before',
      render: (amount) => formatCurrency(amount),
      width: 120,
    },
    {
      title: 'Saldo Sesudah',
      dataIndex: 'balance_after',
      key: 'balance_after',
      render: (amount) => formatCurrency(amount),
      width: 120,
    },
    {
      title: 'Ref. ID',
      dataIndex: 'reference_id',
      key: 'reference_id',
      width: 100,
    },
    {
      title: 'Deskripsi',
      dataIndex: 'description',
      key: 'description',
    },
  ];

  // Deposit Tickets Table Columns
  const depositColumns = [
    {
      title: 'Nomor Tiket',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      render: (text) => <Text copyable style={{ fontFamily: 'monospace' }}>{text}</Text>,
    },
    {
      title: 'Jumlah',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Jumlah Unik',
      dataIndex: 'unique_amount',
      key: 'unique_amount',
      render: (amount) => <Text strong>{formatCurrency(amount)}</Text>,
    },
    {
      title: 'Bank Tujuan',
      dataIndex: 'bank_account',
      key: 'bank_account',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>,
    },
    {
      title: 'Berlaku Hingga',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Dibuat',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
  ];

  // Settlement Table Columns
  const settlementColumns = [
    {
      title: 'Periode',
      key: 'period',
      render: (record) => (
        <div>
          <div>{dayjs(record.period_start).format('DD/MM/YYYY')}</div>
          <Text type="secondary">s/d {dayjs(record.period_end).format('DD/MM/YYYY')}</Text>
        </div>
      ),
    },
    {
      title: 'Jumlah Transaksi',
      dataIndex: 'total_transactions',
      key: 'total_transactions',
    },
    {
      title: 'Gross Amount',
      dataIndex: 'gross_amount',
      key: 'gross_amount',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Total Fee',
      dataIndex: 'total_fee',
      key: 'total_fee',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Net Amount',
      dataIndex: 'net_amount',
      key: 'net_amount',
      render: (amount) => <Text strong>{formatCurrency(amount)}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>,
    },
    {
      title: 'Settled At',
      dataIndex: 'settled_at',
      key: 'settled_at',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-',
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>Finance Management</Title>
        <Text type="secondary">Kelola keuangan, mutasi saldo, deposit, dan settlement</Text>
      </div>

      {/* Finance Summary Cards - Updated for Deposit Balance System */}
      <Row gutter={24} className="mb-6">
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Saldo Deposit"
              value={formatCurrency(balance.deposit_balance || 0)}
              prefix={<WalletOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Untuk cash out langsung
              </Text>
            </div>
          </Card>
        </Col>
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Saldo Tersedia"
              value={formatCurrency(balance.available_balance || 0)}
              prefix={<BankOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Dari transaksi settle
              </Text>
            </div>
          </Card>
        </Col>
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Saldo Pending"
              value={formatCurrency(balance.pending_balance || 0)}
              prefix={<HistoryOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Menunggu settlement
              </Text>
            </div>
          </Card>
        </Col>
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Total Semua Saldo"
              value={formatCurrency(
                (balance.deposit_balance || 0) +
                (balance.available_balance || 0) +
                (balance.pending_balance || 0)
              )}
              prefix={<WalletOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Total keseluruhan saldo
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Transaction Statistics Cards */}
      <Row gutter={24} className="mb-6">
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Aktivitas Hari Ini"
              value={`${summary.today_activity?.today_transactions || 0} transaksi`}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Kredit: {formatCurrency(summary.today_activity?.today_credit || 0)}
              </Text>
              <br />
              <Text type="secondary">
                Debit: {formatCurrency(summary.today_activity?.today_debit || 0)}
              </Text>
            </div>
          </Card>
        </Col>
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Deposit Pending"
              value={`${summary.pending_deposits?.pending_deposits || 0} tiket`}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Total: {formatCurrency(summary.pending_deposits?.pending_amount || 0)}
              </Text>
            </div>
          </Card>
        </Col>
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Settlement Bulan Ini"
              value={`${summary.monthly_settlements?.settlement_count || 0} kali`}
              prefix={<CreditCardOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Total: {formatCurrency(summary.monthly_settlements?.total_settled || 0)}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Tabs defaultActiveKey="mutations" type="card">
        {/* Balance Mutations Tab */}
        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              Mutasi Saldo
            </span>
          }
          key="mutations"
        >
          <Card title="Laporan Mutasi Saldo" extra={
            <Space>
              <Button icon={<DownloadOutlined />} onClick={handleExportMutations}>Export CSV</Button>
            </Space>
          }>
            {/* Filters */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Select
                  placeholder="Tipe Mutasi"
                  allowClear
                  style={{ width: '100%' }}
                  value={mutationFilters.type}
                  onChange={(value) => setMutationFilters({ ...mutationFilters, type: value || '' })}
                >
                  <Option value="credit">Kredit</Option>
                  <Option value="debit">Debit</Option>
                </Select>
              </Col>
              <Col span={6}>
                <Select
                  placeholder="Kategori"
                  allowClear
                  style={{ width: '100%' }}
                  value={mutationFilters.reference_type}
                  onChange={(value) => setMutationFilters({ ...mutationFilters, reference_type: value || '' })}
                >
                  <Option value="deposit">Deposit</Option>
                  <Option value="withdrawal">Withdrawal</Option>
                  <Option value="transaction_fee">Transaction Fee</Option>
                  <Option value="settlement">Settlement</Option>
                  <Option value="refund">Refund</Option>
                  <Option value="adjustment">Adjustment</Option>
                </Select>
              </Col>
              <Col span={8}>
                <RangePicker
                  style={{ width: '100%' }}
                  onChange={(dates) => {
                    setMutationFilters({
                      ...mutationFilters,
                      date_from: dates?.[0]?.format('YYYY-MM-DD') || '',
                      date_to: dates?.[1]?.format('YYYY-MM-DD') || ''
                    });
                  }}
                />
              </Col>
              <Col span={4}>
                <Button type="primary" onClick={fetchBalanceMutations} style={{ width: '100%' }}>
                  Filter
                </Button>
              </Col>
            </Row>

            <Table
              columns={mutationColumns}
              dataSource={balanceMutations}
              rowKey="id"
              loading={loading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} items`,
                onChange: (page, pageSize) => {
                  setMutationFilters({ ...mutationFilters, page, limit: pageSize });
                }
              }}
              scroll={{ x: 1000 }}
            />
          </Card>
        </TabPane>

        {/* Deposit Tickets Tab */}
        <TabPane
          tab={
            <span>
              <BankOutlined />
              Deposit
            </span>
          }
          key="deposits"
        >
          <Card title="Tiket Deposit" extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateDepositVisible(true)}>
              Buat Tiket Deposit
            </Button>
          }>
            <Table
              columns={depositColumns}
              dataSource={depositTickets}
              rowKey="id"
              scroll={{ x: 1000 }}
            />
          </Card>
        </TabPane>

        {/* Settlements Tab */}
        <TabPane
          tab={
            <span>
              <CreditCardOutlined />
              Settlement
            </span>
          }
          key="settlements"
        >
          <Card title="Settlement Records">
            <Table
              columns={settlementColumns}
              dataSource={settlements}
              rowKey="id"
              scroll={{ x: 800 }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Create Deposit Ticket Modal */}
      <Modal
        title="Buat Tiket Deposit"
        open={createDepositVisible}
        onCancel={() => {
          setCreateDepositVisible(false);
          depositForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Alert
          message="Informasi"
          description="Sistem akan menambahkan kode unik 3 digit pada jumlah deposit untuk verifikasi otomatis."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={depositForm}
          layout="vertical"
          onFinish={handleCreateDepositTicket}
        >
          <Form.Item
            label="Jumlah Deposit"
            name="amount"
            rules={[
              { required: true, message: 'Jumlah deposit diperlukan!' },
              { type: 'number', min: 10000, message: 'Minimal deposit Rp 10,000!' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/Rp\s?|(,*)/g, '')}
              placeholder="Masukkan jumlah deposit"
            />
          </Form.Item>

          <Form.Item
            label="Bank Tujuan"
            name="bank_account"
            rules={[{ required: true, message: 'Bank tujuan diperlukan!' }]}
          >
            <Select placeholder="Pilih bank tujuan">
              {depositBanks.map((bank) => (
                <Option key={bank.id} value={`${bank.bank_name} - ${bank.account_number}`}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {bank.logo_url && (
                      <img
                        src={bank.logo_url}
                        alt={bank.bank_name}
                        style={{ width: 24, height: 24, marginRight: 8 }}
                      />
                    )}
                    {bank.bank_name} - {bank.account_number}
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                Buat Tiket
              </Button>
              <Button onClick={() => setCreateDepositVisible(false)}>
                Batal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MerchantFinance;